import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { FaChevronDown, FaQuestionCircle } from 'react-icons/fa';

const FAQSection = () => {
  const { language } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(null);

  const faqs = {
    en: [
      {
        q: "What is COSMO Personality?",
        a: "COSMO Personality is a comprehensive B2B platform created by clinicians for clinicians. It integrates multiple assessment methods into a single digital environment. Our goal is to bridge the gap between psychological testing and therapeutic intervention, providing tools that are not only psychometrically sound but also highly useful for collaborative feedback and treatment planning."
      },
      {
        q: "What is the difference between Self Report and Typical Performance tools?",
        a: "Self-report tests (like PID-5-BF+ or ETMCQ) ask patients to rate their own conscious behaviors and feelings, which is great for assessing symptom severity directly. Typical Performance tools (like PFS or TCTS) are projective-associative; they bypass conscious defenses by asking patients to respond to ambiguous stimuli. Combining them provides a true 360-degree view, revealing both what the patient knows about themselves and what might be driving their behavior unconsciously."
      },
      {
        q: "What is the COSMO Coding System for the Picture Frustration Study?",
        a: "The COSMO method modernizes the classic Rosenzweig PFS. It maps patient responses onto a grid based on interpersonal style (Friendly/Cooperative to Hostile/Dominant) and coping strategy. This closely aligns with the DSM-5 Alternative Model for Personality Disorders (AMPD) Criterion B. It gives clinicians a visual, easy-to-understand profile of how a patient reacts to frustration, which is excellent for discussing relational patterns directly with the patient in collaborative assessment."
      },
      {
        q: "Are there training courses available?",
        a: "Yes, we offer a dedicated academy. Our premium video courses cover specific instruments (like the Compass of Shame Scale, LPF Scale 2.0, or ETMCQ). They are hosted on a user-friendly platform with secure Stripe payments. Courses include practical clinical examples, coding instructions, and completion certificates, allowing you to immediately apply these tools confidently in your practice."
      },
      {
        q: "Do you provide free resources?",
        a: "We strongly believe in accessible education. Our library includes summaries of key psychological research. To accommodate different learning styles, each topic is presented in three formats: a detailed Text/Blog post for deep dives, a visual Mind Map for quick review, and an Audio Podcast to listen to while commuting. Topics range from Family Therapy to Personality Assessment."
      },
      {
        q: "How does the Interactive Dashboard work?",
        a: "The Interactive Dashboard is the core of our platform. When you input patient test scores, the dashboard generates clear, comparative visual graphs. It uses our intuitive \"COSMO Attention Levels\" (represented by mascot heads: Normal, Attention, High Attention) to quickly flag areas needing clinical focus. This allows you to compare self-report data with typical performance data side-by-side, facilitating a deeper diagnostic understanding."
      }
    ],
    it: [
      {
        q: "Cos'è COSMO Personality?",
        a: "COSMO Personality è una piattaforma B2B completa creata da clinici per i clinici. Integra molteplici metodi di valutazione in un unico ambiente digitale. Il nostro obiettivo è colmare il divario tra i test psicologici e l'intervento terapeutico, fornendo strumenti che non sono solo validi psicometricamente, ma anche altamente utili per il feedback collaborativo e la pianificazione del trattamento."
      },
      {
        q: "Qual è la differenza tra strumenti di Autovalutazione e di Performance Tipica?",
        a: "I test di autovalutazione (come PID-5-BF+ o ETMCQ) chiedono ai pazienti di valutare i propri comportamenti e sentimenti consci, il che è ottimo per valutare direttamente la gravità dei sintomi. Gli strumenti di Performance Tipica (come PFS o TCTS) sono proiettivo-associativi; aggirano le difese consce chiedendo ai pazienti di rispondere a stimoli ambigui. Combinarli fornisce una vera visione a 360 gradi, rivelando sia ciò che il paziente sa di sé sia ciò che potrebbe guidare inconsciamente il suo comportamento."
      },
      {
        q: "Cos'è il Sistema di Codifica COSMO per il Picture Frustration Study?",
        a: "Il metodo COSMO modernizza il classico PFS di Rosenzweig. Mappa le risposte dei pazienti su una griglia basata sullo stile interpersonale (da Amichevole/Cooperativo a Ostile/Dominante) e sulla strategia di coping. Questo si allinea strettamente con il Criterio B del Modello Alternativo per i Disturbi di Personalità (AMPD) del DSM-5. Fornisce ai clinici un profilo visivo e facile da capire di come un paziente reagisce alla frustrazione, eccellente per discutere modelli relazionali direttamente con il paziente nella valutazione collaborativa."
      },
      {
        q: "Ci sono corsi di formazione disponibili?",
        a: "Sì, offriamo un'accademia dedicata. I nostri video corsi premium coprono strumenti specifici (come la Compass of Shame Scale, LPF Scale 2.0 o ETMCQ). Sono ospitati su una piattaforma intuitiva con pagamenti sicuri tramite Stripe. I corsi includono esempi clinici pratici, istruzioni di codifica e certificati di completamento, permettendoti di applicare immediatamente questi strumenti con sicurezza nella tua pratica."
      },
      {
        q: "Fornite risorse gratuite?",
        a: "Crediamo fermamente nell'educazione accessibile. La nostra libreria include riassunti di importanti ricerche psicologiche. Per accogliere diversi stili di apprendimento, ogni argomento è presentato in tre formati: un Testo/Blog post dettagliato per approfondire, una Mappa Mentale visiva per una rapida revisione, e un Podcast Audio da ascoltare in movimento. Gli argomenti spaziano dalla Terapia Familiare all'Assessment della Personalità."
      },
      {
        q: "Come funziona la Dashboard Interattiva?",
        a: "La Dashboard Interattiva è il cuore della nostra piattaforma. Quando inserisci i punteggi dei test dei pazienti, la dashboard genera grafici visivi chiari e comparativi. Utilizza i nostri intuitivi \"Livelli di Attenzione COSMO\" (rappresentati dalle teste della mascotte: Normale, Attenzione, Alta Attenzione) per segnalare rapidamente le aree che necessitano di focus clinico. Questo ti permette di confrontare i dati di autovalutazione con i dati di performance tipica fianco a fianco, facilitando una comprensione diagnostica più profonda."
      }
    ]
  };

  const currentFaqs = faqs[language] || faqs.en;

  const toggleFaq = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div id="faq-section" className="py-20 bg-gray-50 border-t border-gray-200">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center mb-4">
            <FaQuestionCircle className="text-4xl text-indigo-600 mr-3" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              {language === 'en' ? 'Frequently Asked Questions' : 'Domande Frequenti'}
            </h2>
          </div>
          <p className="text-xl text-gray-600">
            {language === 'en' 
              ? 'Everything you need to know about the COSMO Personality platform and tools.' 
              : 'Tutto ciò che devi sapere sulla piattaforma e sugli strumenti COSMO Personality.'}
          </p>
        </motion.div>

        <div className="space-y-4">
          {currentFaqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
            >
              <button
                onClick={() => toggleFaq(index)}
                className="w-full px-6 py-4 flex items-center justify-between focus:outline-none hover:bg-gray-50 transition-colors"
                aria-expanded={activeIndex === index}
              >
                <span className="text-lg font-semibold text-gray-900 text-left">
                  {faq.q}
                </span>
                <motion.div
                  animate={{ rotate: activeIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="ml-4 flex-shrink-0 text-indigo-600"
                >
                  <FaChevronDown />
                </motion.div>
              </button>
              <AnimatePresence>
                {activeIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="px-6 pb-5 pt-2 border-t border-gray-100 text-gray-600 leading-relaxed text-base">
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FAQSection;